// src/ui/overlays/DevOverlay.tsx — D-11 / D-12 / VIS-03 debug overlay.
//
// Subscribes to renderBus:
//   - 'sim-tick' + 'render-fps' (P0 signals; P3 payload carries round + player
//     fields — drives the Round readout row)
//   - 'zone-labels' + 'smoke-test' (P1 Plan 03 signals; D-12 dev-only zone names)
//   - 'lighting-debug' (P2 Plan 02-03 — synthetic per-sim-tick snapshot
//     emitted by GameScene.update() carrying { torchOn, fovTileCount,
//     motionLights[], tick }); drives the Torch + FOV + Lights rows.
//
// Invariants (STATE.md locked):
//   - ALL hooks declared unconditionally at the top. The debug gate is the FIRST
//     line of the render body AFTER all hooks. Placing a hook inside a
//     conditional branch triggers React's "Rendered fewer hooks than expected"
//     runtime error.
//   - The overlay is gated at RUNTIME by isDebugEnabled() (src/debug-flag.ts).
//     In production builds the code ships but the overlay renders null unless
//     the user has set ?debug=1 or localStorage['hunter:debug']='1'.
//     In dev / Vitest (import.meta.env.DEV) the overlay is always visible.
//
// P3 Plan 03-08 extension: DEV-only character picker + round readout.
//
// Source: Phase 2 UI-SPEC §DevOverlay P2 extension; 02-03-PLAN.md Task 2 Step 3c;
//         Phase 3 UI-SPEC §DevOverlay extension; 03-CONTEXT.md D-27.

import { useEffect, useState, type JSX } from 'react';
import { isDebugEnabled } from '../../debug-flag';
import { renderBus, type MotionLightDebugEntry } from '../../render/bus';
import { fearToHeartbeatBpm } from '../fear-feedback/fear-curve';
import {
  P6_BUBBLE_CAP,
  P6_FEAR_THRESHOLD_NERVOUS,
  P6_FEAR_THRESHOLD_AFRAID,
  P6_FEAR_THRESHOLD_TERRIFIED,
} from '../../render/audio/constants';
// DEBUG exception to WARNING-15 tier isolation: DevOverlay is gated at
// runtime by isDebugEnabled() (ships in prod but returns null unless
// ?debug=1 or localStorage['hunter:debug']='1'). The player-facing HUD
// (RoundTimer, StaminaBar, HideExitButton, OnFloorDropButton, …) remains
// fully profile-free. P5 Plan 08 (Pitfall 1): the direct PROFILES record
// import was removed; this picker now reads the live character id list via
// listProfileIds() + hasProfile(), which pull from the hydrated content
// cache. Do NOT add any new sim-tier imports here without a matching
// "DEV-ONLY exception" block citing the same rationale.
import { hasProfile, listProfileIds } from '../../sim/characters/profiles';
import { hasContentBundle } from '../../sim/content/cache';
import type { RoundState, BlackboardEntry } from '../../shared/types/world';
import './overlays.css';

/** P4 Plan 04-10 (Task 3): AI debug snapshot from 'ai-debug' renderBus event.
 *  blackboardHunter/Hider carry the live entries; fears is a per-entity fear map.
 *  Null until the first ai-debug event arrives so the section doesn't flash on boot. */
interface AiDebugSnapshot {
  blackboardHunter: BlackboardEntry[];
  blackboardHider: BlackboardEntry[];
  fears: Record<string, number>;
  tick: number;
}

const SESSION_KEY = 'hunter:dev.character';
const DEFAULT_CHAR = 'charlie';

interface DebugState {
  tick: number;
  fps: number;
  seed: number;
  simTimeMs: number;
}

/** P3 UAT Test 7 follow-up: surface the player's role + status + stamina
 *  so the playtester can see peek/hide transitions (there is no visible
 *  FOV cone for peek in P3 — that renders in P4 polish). */
interface PlayerDebugState {
  role: 'hunter' | 'hider';
  status: 'free' | 'hiding' | 'peeking' | string;
  stamina: number;
  characterId: string;
}

/** Initial value for the P2 lighting-debug snapshot; null until the first
 * sim tick emits so the Torch/FOV/Pos rows only render once real data lands. */
interface LightingDebugSnapshot {
  torchOn: boolean;
  fovTileCount: number;
  motionLights: MotionLightDebugEntry[];
  tick: number;
  playerPosition: {
    world: { x: number; y: number };
    tile: { x: number; y: number } | null;
  };
}

export function DevOverlay(): JSX.Element | null {
  // ===== HOOKS (unconditional; declared BEFORE the DEV gate) =====
  // Hook order must stay consistent across renders, so we always register
  // the hooks; the DEV gate guards the render output.
  const [state, setState] = useState<DebugState>({
    tick: 0,
    fps: 0,
    seed: 0,
    simTimeMs: 0,
  });
  const [player, setPlayer] = useState<PlayerDebugState | null>(null);

  // P2 additions — single snapshot populated from the 'lighting-debug' event.
  // Null until the first tick emits so the rows don't render with zero-fill
  // before the sim has run at all.
  const [lighting, setLighting] = useState<LightingDebugSnapshot | null>(null);

  // P3 Plan 03-08: dev-only character picker (D-27). Persists the selection
  // across reloads via sessionStorage. The selected id is consumed by the sim
  // at createInitialWorldState on the NEXT page reload — hot-swap between
  // rounds is deferred to P5 (CHAR-07).
  // TODO(P5 CHAR-07): hot-swap character on next round-started without reload.
  const [selectedCharacter, setSelectedCharacter] = useState<string>(() => {
    try {
      const persisted = sessionStorage.getItem(SESSION_KEY);
      if (persisted && hasProfile(persisted)) return persisted;
    } catch {
      // sessionStorage may throw in sandboxed iframes; fall through to default.
    }
    return DEFAULT_CHAR;
  });

  // P3 Plan 03-08: round readout row — phase + round-number from sim-tick.
  const [round, setRound] = useState<RoundState | null>(null);

  // P4 Plan 04-10 (Task 3): AI debug snapshot from GameScene's synthetic ai-debug event.
  const [aiDebug, setAiDebug] = useState<AiDebugSnapshot | null>(null);

  // P11 Plan 11-04 (D-01): per-NPC AI state from the extended ai-debug npcBranches field.
  // Declared UNCONDITIONALLY before the isDebugEnabled() gate (Rules-of-Hooks / Pitfall 5).
  // Null until the first ai-debug event with npcBranches arrives.
  const [npcAi, setNpcAi] = useState<Record<string, {
    branch: string;
    torch: boolean;
    awareness: string;
    checkedSpots: number;
  }> | null>(null);

  // P6 Plan 06-07: DEV-only telemetry — concurrent Howler source count.
  // Null until the first audio-debug event lands so the row doesn't flash
  // 'Audio: 0 howls live' before the manager has run a sim-tick.
  const [audioDebug, setAudioDebug] = useState<{ liveSources: number } | null>(null);
  // P6 Plan 06-07: simple cumulative bubble counter — increments on every
  // 'bubble-shown' emit. Used by DevOverlay to render the bubble row in the
  // 'N/CAP' format (UI-SPEC §DevOverlay P6 Extensions).
  const [bubbleCount, setBubbleCount] = useState<number>(0);

  useEffect(() => {
    if (!isDebugEnabled()) return;
    const offTick = renderBus.on('sim-tick', (e) => {
      setState((s) => ({
        ...s,
        tick: e.tick,
        simTimeMs: e.simTimeMs,
        seed: e.seed,
      }));
      // P3 Plan 03-08: surface round state for the debug readout row.
      setRound(e.round);
      // P3 UAT Test 7 follow-up: role/status/stamina snapshot.
      setPlayer({
        // narrow: hunter|hider only — dogs/adults never drive the dev overlay player row.
        role: e.playerRole === 'hunter' ? 'hunter' : 'hider',
        status: e.playerStatus,
        stamina: e.playerStamina,
        characterId: e.characterId,
      });
    });
    const offFps = renderBus.on('render-fps', (e) => {
      setState((s) => ({ ...s, fps: e.fps }));
    });
    // P2: single subscription — the payload carries torchOn + fovTileCount +
    // motionLights[] in one shot per sim tick (30 Hz).
    const offLighting = renderBus.on('lighting-debug', (e) => {
      setLighting({
        torchOn: e.torchOn,
        fovTileCount: e.fovTileCount,
        motionLights: e.motionLights,
        tick: e.tick,
        playerPosition: e.playerPosition,
      });
    });
    // P4 Plan 04-10 (Task 3): AI debug snapshot — blackboards + fear map.
    // Emitted by GameScene under isDebugEnabled() guard (gated at runtime).
    // P11 Plan 11-04 (D-01): also update npcAi from the extended npcBranches field.
    const offAiDebug = renderBus.on('ai-debug', (e) => {
      setAiDebug({
        blackboardHunter: e.blackboardHunter,
        blackboardHider: e.blackboardHider,
        fears: e.fears,
        tick: e.tick,
      });
      setNpcAi(e.npcBranches ?? null);
    });
    // P6 Plan 06-07 (DEV-only): live Howler source count, forwarded by the
    // audio manager from getLiveSources() under its own DEV gate.
    const offAudioDebug = renderBus.on('audio-debug', (e) => {
      setAudioDebug({ liveSources: e.liveSources });
    });
    // P6 Plan 06-07 (DEV-only): bubble-shown counter — emitted by BubbleLayer
    // (or whichever layer eventually wires it) every time a bubble is admitted.
    const offBubbleShown = renderBus.on('bubble-shown', () => {
      setBubbleCount((c) => c + 1);
    });
    return () => {
      offTick();
      offFps();
      offLighting();
      offAiDebug();
      offAudioDebug();
      offBubbleShown();
    };
  }, []);

  // ===== DEBUG GATE (FIRST line of render body, AFTER all hooks) =====
  // Gated at runtime by isDebugEnabled() so the overlay is available in
  // production builds behind ?debug=1 or localStorage['hunter:debug']='1'.
  if (!isDebugEnabled()) return null;

  // P5 gap closure (UAT Test 1, Plan 05-09): DevOverlay mounts at every App
  // stage — including stage==='loading' — BEFORE useContent has run
  // hydrateContent(). listProfileIds() below reads the content cache and
  // throws "Content not hydrated" while cached === null. Early-return until
  // hydration lands (arrives within ms on any boot; useContent's fallback
  // branch guarantees DEFAULT_BUNDLE even on network failure). Mirrors
  // CharacterSelect.tsx:75. Guard lives BELOW hooks + AFTER DEV gate to
  // preserve the hook-order invariant (lines 13-18 comment).
  if (!hasContentBundle()) return null;

  const onPickCharacter = (id: string): void => {
    setSelectedCharacter(id);
    try {
      sessionStorage.setItem(SESSION_KEY, id);
    } catch {
      // sessionStorage may throw in sandboxed iframes; ignore silently.
    }
  };

  // ===== RENDER =====
  return (
    <div
      className="dev-overlay"
      data-testid="dev-overlay"
      aria-hidden="true"
    >
      <div>Tick: {state.tick}</div>
      <div>FPS: {state.fps}</div>
      <div>Seed: {state.seed}</div>
      <div>SimTime: {state.simTimeMs}</div>
      {round !== null && (
        <div
          className="dev-overlay__round-readout"
          data-testid="round-readout"
        >
          Round: {round.roundNumber} · {round.phase}
        </div>
      )}
      {player !== null && (
        <div data-testid="player-row">
          Player: {player.characterId} · {player.role} · {player.status}
          {' · stamina '}
          {Math.round(player.stamina * 100)}%
        </div>
      )}
      <div
        className="dev-overlay__char-picker"
        data-testid="dev-char-picker"
      >
        {listProfileIds().map((id) => (
          <button
            key={id}
            type="button"
            className="dev-overlay__char-chip"
            data-active={selectedCharacter === id ? 'true' : 'false'}
            onClick={() => onPickCharacter(id)}
          >
            {id}
          </button>
        ))}
      </div>
      {/* TODO(P7): DEV foxguard readout (streak len / conditions) once the
       *  sim-tick payload carries the nearest hunter's foxguard streak —
       *  P3 MVP keeps the debug ring visual (DEV-only Phaser Graphics in
       *  GameScene) and defers the readable number to P7 tuning. */}
      {/* P2 additions — rendered only once the first lighting-debug event
          arrives so boot-state doesn't flash "Pos: 0,0" before sim has run. */}
      {lighting !== null && (
        <>
          <div data-testid="pos-row">
            Pos: ({Math.round(lighting.playerPosition.world.x)},{' '}
            {Math.round(lighting.playerPosition.world.y)}) px
            {' · tile '}
            {lighting.playerPosition.tile
              ? `(${lighting.playerPosition.tile.x}, ${lighting.playerPosition.tile.y})`
              : '(—)'}
          </div>
          <div data-testid="torch-row">Torch: {lighting.torchOn ? 'on' : 'off'}</div>
          <div data-testid="fov-row">
            FOV: e0 {lighting.torchOn ? 'on' : 'off'} ({lighting.fovTileCount} tiles, tick={lighting.tick})
          </div>
          {lighting.motionLights.length > 0 && (
            <div
              className="dev-overlay__lights-section"
              data-testid="lights-section"
            >
              <div>Lights ({lighting.motionLights.length}):</div>
              {lighting.motionLights.map((L) => (
                <div key={L.id} className="dev-overlay__light-row">
                  <span
                    className="dev-overlay__light-ring"
                    data-state={L.state}
                  />
                  <span>{L.id}</span>
                  <span>[{L.state}]</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {/* P6 Plan 06-07 — DEV-only audio source count row (audio-debug event). */}
      {audioDebug !== null && (
        <div className="dev-overlay__audio-row" data-testid="audio-row">
          Audio: {audioDebug.liveSources} howls live
        </div>
      )}
      {/* P6 Plan 06-07 — DEV-only fear+BPM row. Reads e0 fear from the
       *  ai-debug payload + maps to threshold + curve-derived BPM. */}
      {aiDebug?.fears?.['e0'] !== undefined && (
        <div className="dev-overlay__fear-bpm-row" data-testid="fear-bpm-row">
          {(() => {
            const fear = aiDebug.fears['e0']!;
            const threshold =
              fear >= P6_FEAR_THRESHOLD_TERRIFIED ? 'terrified' :
              fear >= P6_FEAR_THRESHOLD_AFRAID ? 'afraid' :
              fear >= P6_FEAR_THRESHOLD_NERVOUS ? 'nervous' : 'none';
            const bpm = Math.round(fearToHeartbeatBpm(fear));
            return `Fear: e0 ${fear.toFixed(2)} · ${threshold} · ${bpm} BPM`;
          })()}
        </div>
      )}
      {/* P6 Plan 06-07 — DEV-only bubble row. Cumulative bubble-shown count
       *  rendered as N/CAP so the cap-3 ceiling is visible at a glance. */}
      <div className="dev-overlay__bubble-row" data-testid="bubble-row">
        Bubbles: {bubbleCount}/{P6_BUBBLE_CAP}
      </div>
      {/* P11 Plan 11-04 (D-01): NPC AI state text panel. Debug-flag-gated — never
          renders for family players (D-02 / T-11-04-01 info-disclosure mitigation).
          Appears only after the first ai-debug event with npcBranches arrives. */}
      {npcAi !== null && (
        <div
          data-testid="npc-ai-panel"
          style={{ fontFamily: 'monospace', fontSize: 10 }}
        >
          <strong>NPC AI</strong>
          {Object.entries(npcAi).map(([id, s]) => (
            <div key={id}>
              {id}: [{s.branch}] torch={s.torch ? 'ON' : 'off'} {s.awareness} chk={s.checkedSpots}
            </div>
          ))}
        </div>
      )}
      {/* P4 Plan 04-10 (Task 3, D-11): AI debug section — blackboard visualiser +
          fear readout + telegraph legend. DEV-only; aiDebug is null until first
          ai-debug event arrives. T-04-27: accepted info-disclosure risk (DEV-only). */}
      {aiDebug !== null && (
        <div
          className="dev-overlay__ai-section"
          data-testid="ai-section"
        >
          <div className="dev-overlay__ai-legend">
            AI Telegraph: ?=Suspicious · !=Alerted · !!=Spotted
          </div>
          {/* Blackboard visualiser — one row per hunter-team entry */}
          {aiDebug.blackboardHunter.length > 0 && (
            <div data-testid="blackboard-hunter">
              <div>Hunter BB ({aiDebug.blackboardHunter.length}):</div>
              {aiDebug.blackboardHunter.map((entry) => (
                <div key={entry.id} className="dev-overlay__bb-row">
                  [{entry.confidence}] ({Math.round(entry.position.x)},{Math.round(entry.position.y)})
                  {' age='}{aiDebug.tick - entry.createdAtTick}
                  {' σ='}{entry.initialSigmaPx}
                </div>
              ))}
            </div>
          )}
          {/* Fear readout — one row per entity */}
          {Object.entries(aiDebug.fears).length > 0 && (
            <div data-testid="fear-readout">
              <div>Fear:</div>
              {Object.entries(aiDebug.fears).map(([id, fear]) => {
                const threshold =
                  fear >= 0.9 ? 'terrified' :
                  fear >= 0.65 ? 'afraid' :
                  fear >= 0.35 ? 'nervous' : 'none';
                return (
                  <div key={id} className="dev-overlay__fear-row">
                    {id}: {fear.toFixed(2)} ({threshold})
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
